import React, { useEffect, useRef, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Location } from "../../../@types/DTO/Locations";
import { BranchWithDistance } from "./BranchAddressSearch";

export type MapPanelState = 1 | 2 | 3;

interface BranchMapPanelProps {
  mapState: MapPanelState;
  allLocations: Location[];
  rankedBranches: BranchWithDistance[];
  addressCoords: { lat: number; lng: number } | null;
  selectedBranch: BranchWithDistance | null;
  primaryColor?: string;
  minHeight?: number;
}

// Custom pulse overlay for State 3
function createPulseOverlayClass(position: any, color: string) {
  const OverlayViewBase = (window as any).google.maps.OverlayView;

  return new (class extends OverlayViewBase {
    private _position: any;
    private _div: HTMLDivElement | null = null;
    private _color: string;

    constructor() {
      super();
      this._position = position;
      this._color = color;
    }

    onAdd() {
      this._div = document.createElement("div");
      this._div.style.position = "absolute";
      this._div.style.pointerEvents = "none";
      this._div.innerHTML = `
        <div style="position:relative;width:60px;height:60px;transform:translate(-50%,-50%)">
          <div style="
            position:absolute;inset:0;border-radius:50%;
            background:${this._color};opacity:0.12;
            animation:branchPulse 2s ease-in-out infinite;
          "></div>
          <div style="
            position:absolute;inset:12px;border-radius:50%;
            background:${this._color};opacity:0.2;
          "></div>
          <div style="
            position:absolute;inset:20px;border-radius:50%;
            background:${this._color};
            display:flex;align-items:center;justify-content:center;
          ">
            <div style="width:8px;height:8px;border-radius:50%;background:#fff;"></div>
          </div>
          <div style="
            position:absolute;left:50%;top:100%;
            transform:translateX(-50%);
            width:3px;height:12px;
            background:${this._color};
            border-radius:2px;
            margin-top:-6px;
          "></div>
        </div>
      `;
      const panes = this.getPanes();
      panes?.overlayLayer?.appendChild(this._div);
    }

    draw() {
      if (!this._div) return;
      const projection = this.getProjection();
      if (!projection) return;
      const point = projection.fromLatLngToDivPixel(this._position);
      if (!point) return;
      this._div.style.left = `${point.x}px`;
      this._div.style.top = `${point.y}px`;
    }

    onRemove() {
      this._div?.parentNode?.removeChild(this._div);
      this._div = null;
    }
  })();
}

// Inject keyframe animation once
let animationInjected = false;
function injectPulseAnimation() {
  if (animationInjected) return;
  animationInjected = true;
  const style = document.createElement("style");
  style.textContent = `
    @keyframes branchPulse {
      0%, 100% { opacity: 0.12; transform: scale(1); }
      50%       { opacity: 0.25; transform: scale(1.15); }
    }
  `;
  document.head.appendChild(style);
}

// SVG pin builders
function greyPinSvg(): string {
  return `
    <svg xmlns="http://www.w3.org/2000/svg" width="46" height="62" viewBox="0 0 46 62">
      <circle cx="23" cy="20" r="18" fill="#999" opacity="1"/>
      <circle cx="23" cy="20" r="8" fill="#fff" opacity="1"/>
      <line x1="23" y1="38" x2="23" y2="59" stroke="#999" stroke-width="4" opacity="1"/>
    </svg>
  `;
}

function redPinSvg(label: string, opacity: number): string {
  const w = Math.max(70, label.length * 12 + 30);
  return `
    <svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="78" viewBox="0 0 ${w} 78">
      <circle cx="${w / 2}" cy="20" r="18" fill="#E24B4A" opacity="${opacity}"/>
      <circle cx="${w / 2}" cy="20" r="8" fill="#fff" opacity="${opacity}"/>
      <line x1="${w / 2}" y1="38" x2="${
    w / 2
  }" y2="50" stroke="#E24B4A" stroke-width="4" opacity="${opacity}"/>
      <rect x="2" y="50" width="${w - 4}" height="24" rx="5"
        fill="rgba(255,255,255,0.92)" opacity="${opacity}"/>
      <text x="${w / 2}" y="66" text-anchor="middle" font-size="13"
        fill="#E24B4A" font-family="Roboto, Arial, sans-serif" font-weight="600"
        opacity="${opacity}">${label}</text>
    </svg>
  `;
}

// Component
const BranchMapPanel: React.FC<BranchMapPanelProps> = ({
  mapState,
  allLocations,
  rankedBranches,
  addressCoords,
  selectedBranch,
  primaryColor = "#E24B4A",
  minHeight,
}) => {
  const { t: translate } = useTranslation();
  const mapDivRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<any>(null);
  const markersRef = useRef<any[]>([]);
  const addressMarkerRef = useRef<any>(null);
  const pulseOverlayRef = useRef<any>(null);
  const [mapsReady, setMapsReady] = useState<boolean>(false);

  // Build / init map once
  const initMap = useCallback(() => {
    if (!mapDivRef.current || !(window as any).google?.maps || mapRef.current)
      return;
    injectPulseAnimation();

    const g = (window as any).google.maps;
    mapRef.current = new g.Map(mapDivRef.current, {
      zoom: 6,
      // Default centre: Italy
      center: { lat: 41.9028, lng: 12.4964 },
      disableDefaultUI: true,
      zoomControl: true,
      gestureHandling: "cooperative",
      styles: [
        { featureType: "poi", stylers: [{ visibility: "off" }] },
        { featureType: "transit", stylers: [{ visibility: "off" }] },
        {
          featureType: "road",
          elementType: "geometry",
          stylers: [{ color: "#ffffff" }],
        },
        {
          featureType: "landscape",
          stylers: [{ color: "#e8ead3" }],
        },
        {
          featureType: "water",
          stylers: [{ color: "#b8d0e8" }],
        },
      ],
    });
  }, []);

  // Clear all markers/overlays
  const clearMarkers = useCallback(() => {
    markersRef.current.forEach((m) => m.setMap(null));
    markersRef.current = [];
    if (addressMarkerRef.current) {
      addressMarkerRef.current.setMap(null);
      addressMarkerRef.current = null;
    }
    if (pulseOverlayRef.current) {
      pulseOverlayRef.current.setMap(null);
      pulseOverlayRef.current = null;
    }
  }, []);

  // Render State 1: grey pins for all branches
  const renderState1 = useCallback(() => {
    if (!mapRef.current) return;
    clearMarkers();

    const map = mapRef.current;
    const g = (window as any).google.maps;
    // Only render pins with valid Italian coordinates (lat 35–48, lng 5–20)
    const locationsWithCoords = allLocations.filter(
      (loc) =>
        loc.latitude != null &&
        loc.longitude != null &&
        loc.latitude > 20 &&
        loc.latitude < 48 &&
        loc.longitude > 5 &&
        loc.longitude < 20
    );

    if (!locationsWithCoords.length) {
      map.setCenter({ lat: 41.9028, lng: 12.4964 });
      map.setZoom(6);
      return;
    }

    const bounds = new g.LatLngBounds();
    locationsWithCoords.forEach((loc) => {
      const pos = { lat: loc.latitude, lng: loc.longitude };
      bounds.extend(pos);

      const marker = new g.Marker({
        position: pos,
        map,
        icon: {
          url:
            "data:image/svg+xml;charset=UTF-8," +
            encodeURIComponent(greyPinSvg()),
          scaledSize: new g.Size(46, 62),
          anchor: new g.Point(23, 62),
        },
        title: loc.locationName,
        optimized: false,
      });
      markersRef.current.push(marker);
    });

    map.fitBounds(bounds, 40);
    g.event.addListenerOnce(map, "idle", () => {
      if ((map.getZoom() ?? 6) < 6) map.setZoom(6);
    });
  }, [allLocations, clearMarkers]);

  const renderState2 = useCallback(() => {
    if (!mapRef.current || !addressCoords) return;
    clearMarkers();

    const map = mapRef.current;
    const g = (window as any).google.maps;

    const displayBranches = rankedBranches.slice(0, 5);
    const total = displayBranches.length;
    const bounds = new g.LatLngBounds();

    bounds.extend({ lat: addressCoords.lat, lng: addressCoords.lng });
    const addressMarker = new g.Marker({
      position: { lat: addressCoords.lat, lng: addressCoords.lng },
      map,
      icon: {
        path: g.SymbolPath.CIRCLE,
        scale: 8,
        fillColor: "#4285F4",
        fillOpacity: 1,
        strokeColor: "#fff",
        strokeWeight: 2,
      },
      title: translate("Your address"),
      zIndex: 10,
    });
    addressMarkerRef.current = addressMarker;

    const opacities = [1.0, 0.85, 0.7, 0.55, 0.45];

    displayBranches.forEach(({ location, distanceKm, rank }) => {
      if (location.latitude == null || location.longitude == null) return;

      const pos = { lat: location.latitude, lng: location.longitude };
      bounds.extend(pos);

      const opacity = opacities[rank] ?? 0.45;

      const label = `${distanceKm} km`;
      const svgUrl =
        "data:image/svg+xml;charset=UTF-8," +
        encodeURIComponent(redPinSvg(label, opacity));

      const w = Math.max(70, label.length * 12 + 30);
      const marker = new g.Marker({
        position: pos,
        map,
        icon: {
          url: svgUrl,
          scaledSize: new g.Size(w, 78),
          anchor: new g.Point(w / 2, 78),
        },
        title: `${location.locationName} — ${distanceKm} km`,
        zIndex: total - rank, // nearest branch renders on top
        optimized: false,
      });
      markersRef.current.push(marker);
    });

    map.fitBounds(bounds, 60);
  }, [rankedBranches, addressCoords, clearMarkers]);

  // Render State 3: single pulsing pin
  const renderState3 = useCallback(() => {
    if (!mapRef.current || !selectedBranch) return;
    clearMarkers();

    const { location, distanceKm } = selectedBranch;
    if (location.latitude == null || location.longitude == null) return;

    const map = mapRef.current;
    const g = (window as any).google.maps;
    const pos = new g.LatLng(location.latitude, location.longitude);

    // Zoom in on the selected branch
    map.setCenter(pos);
    map.setZoom(15);
    const overlay = createPulseOverlayClass(pos, "#E24B4A");
    overlay.setMap(map);
    pulseOverlayRef.current = overlay;
  }, [selectedBranch, clearMarkers]);

  // Re-render when state or data changes
  useEffect(() => {
    if (!mapsReady || !mapRef.current) return;
    const timeout = setTimeout(() => {
      if (mapState === 1) renderState1();
      else if (mapState === 2) renderState2();
      else if (mapState === 3) renderState3();
    }, 50);
    return () => clearTimeout(timeout);
  }, [
    mapsReady,
    mapState,
    allLocations,
    rankedBranches,
    selectedBranch,
    renderState1,
    renderState2,
    renderState3,
  ]);

  useEffect(() => {
    if ((window as any).google?.maps) {
      initMap();
      setMapsReady(true);
      return;
    }
    const poll = setInterval(() => {
      if ((window as any).google?.maps) {
        clearInterval(poll);
        initMap();
        setMapsReady(true);
      }
    }, 300);
    return () => clearInterval(poll);
  }, [initMap]);

  useEffect(() => {
    return () => {
      clearMarkers();
      const g = (window as any).google?.maps;
      if (g && mapRef.current) {
        g.event.clearInstanceListeners(mapRef.current);
      }
      mapRef.current = null;
    };
  }, [clearMarkers]);

  const infoBranch = mapState === 3 ? selectedBranch : null;

  return (
    <div
      style={{
        position: "relative",
        height: "100%",
        minHeight: minHeight ?? 420,
        overflow: "hidden",
        borderLeft: "0.5px solid #e0e0e0",
        borderRadius: "0 8px 8px 0",
        background: "#e8ead3",
      }}
    >
      {/* Google Map canvas */}
      <div ref={mapDivRef} style={{ position: "absolute", inset: 0 }} />

      {/* State 3 info bar */}
      {infoBranch && (
        <div
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            background: "rgba(255,255,255,0.93)",
            borderTop: "0.5px solid #e0e0e0",
            padding: "8px 12px",
            fontSize: 12,
            display: "flex",
            alignItems: "center",
            gap: 6,
            zIndex: 5,
          }}
        >
          <span style={{ color: primaryColor, fontSize: 14 }}>📍</span>
          <span
            style={{
              fontWeight: 500,
              fontSize: 12,
              color: "#222",
              flex: 1,
            }}
          >
            {infoBranch.location.locationName}
          </span>
          <span
            style={{
              fontSize: 11,
              background: "#E6F1FB",
              color: "#185FA5",
              borderRadius: 20,
              padding: "2px 8px",
              fontWeight: 500,
            }}
          >
            {infoBranch.distanceKm} {translate("km away")}
          </span>
        </div>
      )}
    </div>
  );
};

export default BranchMapPanel;
